// The DFT module in a project: a Quantum ESPRESSO band-structure calculation - 1 the recipe and its main settings,
// 2 the pseudopotentials, the checks and the inputs, 3 the run - and its result: the band structure, the lattice, the
// run's summary and files, the density of states (when the recipe asks for it) and the project's runs (show one, start
// from one, compare them). A user can stop there; "Use in SKFitter" makes an SKFitter project from the result.
import { Accordion, Alert, Anchor, Button, Card, Group, Loader, Stack, Text, Tooltip } from "@mantine/core";
import { Link } from "react-router";
import { Markdown } from "../../components/Markdown";
import { resultLine, useExample } from "../../lib/examples";
import { TryList } from "../DftExamples";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { ErrorBox } from "../../components/Errors";
import { patch, type ProjectDetail } from "../../lib/api";
import { useLiveJob, useMe } from "../../lib/hooks";
import { runToSkfitter, type DftMemory, type Recipe } from "../../lib/qe";
import { seedFrom, type DftResult } from "../../lib/skfitter";
import { FeedbackButton } from "../../components/Feedback";
import { DftViews } from "../skfitter/DftStep";
import { QeCalculation } from "./QeCalculation";
import { DosView, RunHistory } from "./RunResults";
import { RecipeStructure } from "./StructureTools";
import { RelaxResult } from "./DefectTools";
import { DefectLevelsView } from "./DefectLevels";
import { DefectFormationView } from "./DefectFormation";
import type { RelaxSummary } from "../../lib/qe";
import { examplesPath } from "../../lib/modules";
import { wannieriseRun } from "../../lib/wannier";

export function DftWorkspace({ project }: { project: ProjectDetail }) {
  const { data: me } = useMe();
  const qc = useQueryClient();
  const nav = useNavigate();
  const saved = project.settings?.dft as DftMemory | undefined;
  const [mem, setMem] = useState<DftMemory>(() => saved ?? { seed: seedFrom(project.name) });
  const [generation, setGeneration] = useState(0);         // a new recipe from a run: the calculation starts again

  // remember the recipe, the seedname and the last job in the project
  const latest = useRef(mem);
  latest.current = mem;
  const remember = useCallback((next: Partial<DftMemory>) => {
    const merged = { ...latest.current, ...next };
    setMem(merged);
    patch(`/projects/${project.id}`, { settings: { ...(project.settings ?? {}), dft: merged } })
      .then(() => qc.invalidateQueries({ queryKey: ["project", project.id] }))
      .catch(() => undefined);
  }, [project.id, project.settings, qc]);
  const update = useCallback((next: Partial<DftMemory>) => setMem((m) => ({ ...m, ...next })), []);

  const { job } = useLiveJob(mem.job);
  const dft = job?.status === "succeeded" ? (job.result as DftResult) : undefined;
  const toSkfitter = useMutation({
    mutationFn: () => runToSkfitter(job!.id),
    onSuccess: (p) => {
      qc.invalidateQueries({ queryKey: ["projects"] });
      qc.invalidateQueries({ queryKey: ["me"] });
      nav(`/projects/${p.id}`);
    },
  });

  const wannierise = useMutation({
    mutationFn: () => wannieriseRun(project.name, job!.id, dft?.meta.formula ?? dft?.seed ?? "DFT run"),
    onSuccess: (p) => {
      qc.invalidateQueries({ queryKey: ["projects"] });
      qc.invalidateQueries({ queryKey: ["me"] });
      nav(`/projects/${p.id}`);
    },
  });

  if (!me) return <Card withBorder><Loader size="sm" /></Card>;
  const limits = project.limits ?? me.limits;
  if (!limits["dft.enabled"])
    return <Alert color="orange" title="Not included in your plan">DFT calculations are not part of your plan.</Alert>;
  const skfitter = !!limits["skfitter.enabled"];
  const extra = dft as { qe_files?: Record<string, string>; relaxed_recipe?: Recipe; qe?: { relax?: RelaxSummary | null } } | undefined;
  // the cell of the run, in the module's own view: after a relaxation the relaxed one
  const ranRecipe = extra?.relaxed_recipe ?? (job?.params as { recipe?: Recipe } | undefined)?.recipe;
  const hasDos = !!extra?.qe_files?.["qe_dos.json"];
  const relaxed = extra?.qe?.relax;
  const states = (extra?.qe as { defect_states?: { reference?: boolean } | null } | undefined)?.defect_states;
  const hasLevels = !!extra?.qe_files?.["qe_defect_states.json"] && !!states && !states.reference;
  const startFrom = (recipe: Recipe) => {
    remember({ qe: { recipe, template: null, changed: false }, structure_file: undefined });
    setGeneration((g) => g + 1);
  };
  const action = (
    <Stack gap={4} align="flex-end">
      <ErrorBox error={toSkfitter.error ?? wannierise.error} />
      <Group gap="xs">
        <Text size="xs" c="dimmed">A tight-binding model of these bands:</Text>
        <Tooltip disabled={skfitter} label="Tight Binding is not part of your plan">
          <Button onClick={() => toSkfitter.mutate()} loading={toSkfitter.isPending} disabled={!skfitter} data-testid="use-in-skfitter">
            Use in Slater-Koster fit →
          </Button>
        </Tooltip>
        <Tooltip disabled={!!limits["wannier.enabled"]} label="Wannier functions are not part of your plan">
          <Button variant="light" onClick={() => wannierise.mutate()} loading={wannierise.isPending} disabled={!limits["wannier.enabled"]}
            data-testid="wannierise">Wannierise this run →</Button>
        </Tooltip>
      </Group>
    </Stack>
  );
  return (
    <Stack gap="md">
      <ExampleBanner project={project} />
      <QeCalculation key={generation} project={project} mem={mem} update={update} remember={remember} job={job}
        views={dft && job ? <DftViews jobId={job.id} dft={dft} action={action}
          extraTabs={[...(hasDos ? [{ value: "dos", label: "Density of states", node: <DosView jobId={job.id} /> }] : []),
            ...(relaxed ? [{ value: "relax", label: "Relaxation", node: <RelaxResult relax={relaxed}
              onContinue={extra?.relaxed_recipe ? () => startFrom(extra.relaxed_recipe!) : undefined} /> }] : []),
            ...(hasLevels ? [{ value: "levels", label: "Defect levels", node: <DefectLevelsView jobId={job.id} /> },
              { value: "formation", label: "Formation energy", node: <DefectFormationView jobId={job.id} /> }] : [])]}
          lattice={ranRecipe ? <RecipeStructure recipe={ranRecipe} /> : undefined} /> : null} />
      <Group justify="flex-end" mt={-8}>
        <Text size="xs" c="dimmed">Something odd or missing in the DFT module?</Text>
        <FeedbackButton product="dft" context={{ project_id: project.id, job_id: mem.job }} />
      </Group>
      <RunHistory projectId={project.id} current={mem.job} refreshKey={`${job?.id}:${job?.status}`}
        onShow={(id) => remember({ job: id })}
        onDuplicate={(recipe, template) => {
          remember({ qe: { recipe, template, changed: false }, structure_file: undefined });
          setGeneration((g) => g + 1);
        }} />
    </Stack>
  );
}

/** A DFT illustrative project of the user's own (its guide and "try this"), or a project made with "try this". */
function ExampleBanner({ project }: { project: ProjectDetail }) {
  const st = (project.settings ?? {}) as { example?: string; dft?: { from_example?: { id: string; title: string; about: string; notes?: string[] } } };
  const from = st.dft?.from_example;
  const id = st.example ?? from?.id;
  const { data: ex } = useExample(id);
  if (!id) return null;
  if (from) {
    return (
      <Alert color="grape" p="xs" data-testid="from-example">
        <Text size="sm">Made from the illustrative project <Anchor component={Link} to={examplesPath("dft", from.id)}>{ex?.name ?? from.id}</Anchor>:
          {" "}<b>{from.title}</b> - {from.about}</Text>
        {(from.notes ?? []).length > 0 && <Text size="xs" c="dimmed">{from.notes!.join("; ")}</Text>}
        <Text size="xs" c="dimmed">Check the recipe and the estimated time, then run it; compare it with the illustrative project's result
          {ex?.result ? ` (${resultLine(ex.result)})` : ""}.</Text>
      </Alert>
    );
  }
  return (
    <Card withBorder p="sm" data-testid="example-banner">
      <Stack gap={6}>
        <Text size="sm"><b>Illustrative project</b>{ex ? `: ${ex.name}` : ""}. The run below is the finished calculation; look at the band
          structure, the density of states and the run, then try a variation. <Anchor component={Link} to={examplesPath("dft", id)} size="sm">All about it</Anchor></Text>
        {ex && <Accordion variant="contained" chevronPosition="left">
          <Accordion.Item value="guide">
            <Accordion.Control><Text size="sm">Guide: choices and lessons</Text></Accordion.Control>
            <Accordion.Panel><Markdown source={ex.guide ?? ""} /></Accordion.Panel>
          </Accordion.Item>
          <Accordion.Item value="try">
            <Accordion.Control><Text size="sm">Try this ({ex.tries?.length ?? 0})</Text></Accordion.Control>
            <Accordion.Panel><TryList exampleId={ex.id} tries={ex.tries ?? []} /></Accordion.Panel>
          </Accordion.Item>
        </Accordion>}
      </Stack>
    </Card>
  );
}
